import React from 'react';
import { Avatar, Box, Card, Divider, Stack, Typography } from '@mui/material';
import { formatPhoneNumber } from '../../../util/ValidationUtils';
import UserRoleChip from './UserRoleChip';
import SmsIndicator from './SmsIndicator';
import UserActionsMenu from './UserActionsMenu';
import { getRoleChipColors, getUserFullName, getUserInitials, ManagedUser } from './userDisplayUtils';

interface UsersCardsProps {
  users: ManagedUser[];
  isBuyer: boolean;
  roleBasedOptions: string[];
  onEditUser: (user: ManagedUser) => void;
  onDeleteUser: (user: ManagedUser) => void;
}

const DetailLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>
    {children}
  </Typography>
);

/** Mobile-friendly card list — one self-contained card per user (no horizontal scroll). */
const UsersCards = ({ users, isBuyer, roleBasedOptions, onEditUser, onDeleteUser }: UsersCardsProps) => (
  <Stack spacing={1.5}>
    {users.map((user) => {
      const roleColors = getRoleChipColors(user.role);
      const canManage = !!user.role && roleBasedOptions.includes(user.role);

      return (
        <Card key={user.username} variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                fontSize: 14,
                fontWeight: 600,
                bgcolor: roleColors.bg,
                color: roleColors.color,
              }}
            >
              {getUserInitials(user)}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>
                {getUserFullName(user) || user.username}
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <UserRoleChip role={user.role} />
              </Box>
            </Box>
            {canManage && (
              <Box sx={{ flex: 'none' }}>
                <UserActionsMenu user={user} onEdit={onEditUser} onDelete={onDeleteUser} />
              </Box>
            )}
          </Box>

          <Typography
            variant="body2"
            sx={{ mt: 1.5, color: 'text.secondary', overflowWrap: 'anywhere' }}
          >
            {user.username}
          </Typography>

          <Divider sx={{ my: 1.5 }} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: 1.5,
              alignItems: 'center',
            }}
          >
            <Box>
              <DetailLabel>Phone Number</DetailLabel>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.375 }}>
                {formatPhoneNumber(user.phoneNumber || '') || '—'}
              </Typography>
            </Box>
            {isBuyer && (
              <Box sx={{ textAlign: 'right' }}>
                <DetailLabel>SMS</DetailLabel>
                <Box sx={{ mt: 0.5 }}>
                  <SmsIndicator user={user} />
                </Box>
              </Box>
            )}
          </Box>
        </Card>
      );
    })}
  </Stack>
);

export default UsersCards;
